import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SpinWheel from '@/components/SpinWheel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Gift, Frown, Check, Sparkles } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real brand logos as inline SVG components
function GoogleLogo({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.1 24.1 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
  );
}

function InstagramLogo({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <radialGradient id="ig1" cx="19.38" cy="42.04" r="44.9" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fd5"/><stop offset=".328" stopColor="#ff543f"/><stop offset=".348" stopColor="#fc5245"/><stop offset=".504" stopColor="#e64771"/><stop offset=".643" stopColor="#d53e91"/><stop offset=".761" stopColor="#cc39a4"/><stop offset=".841" stopColor="#c837ab"/></radialGradient>
      <path fill="url(#ig1)" d="M34.017 41.99l-20 .019c-4.4.004-8.003-3.592-8.008-7.992l-.019-20c-.004-4.4 3.592-8.003 7.992-8.008l20-.019c4.4-.004 8.003 3.592 8.008 7.992l.019 20c.005 4.4-3.592 8.004-7.992 8.008z"/>
      <radialGradient id="ig2" cx="11.79" cy="2.97" r="29.81" gradientTransform="matrix(1 0 0 .6663 0 .99)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#4168c9"/><stop offset=".999" stopColor="#4168c9" stopOpacity="0"/></radialGradient>
      <path fill="url(#ig2)" d="M34.017 41.99l-20 .019c-4.4.004-8.003-3.592-8.008-7.992l-.019-20c-.004-4.4 3.592-8.003 7.992-8.008l20-.019c4.4-.004 8.003 3.592 8.008 7.992l.019 20c.005 4.4-3.592 8.004-7.992 8.008z"/>
      <path fill="#fff" d="M24 31c-3.859 0-7-3.14-7-7s3.141-7 7-7 7 3.14 7 7-3.141 7-7 7zm0-12c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><circle fill="#fff" cx="31.5" cy="16.5" r="1.5"/><path fill="#fff" d="M30 37H18c-3.859 0-7-3.14-7-7V18c0-3.86 3.141-7 7-7h12c3.859 0 7 3.14 7 7v12c0 3.86-3.141 7-7 7zM18 13c-2.757 0-5 2.243-5 5v12c0 2.757 2.243 5 5 5h12c2.757 0 5-2.243 5-5V18c0-2.757-2.243-5-5-5H18z"/>
    </svg>
  );
}

function FacebookLogo({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48"><linearGradient id="fb1" x1="9.99" x2="40.62" y1="9.99" y2="40.62" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#2aa4f4"/><stop offset="1" stopColor="#007ad9"/></linearGradient><path fill="url(#fb1)" d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z"/><path fill="#fff" d="M26.707 29.301h5.176l.813-5.258h-5.989v-2.874c0-2.184.714-4.121 2.757-4.121H32.8V12.41c-.483-.066-1.509-.217-3.451-.217-4.11 0-6.522 2.162-6.522 7.1v4.749h-4.643v5.258h4.643V44.04c.927.138 1.868.214 2.827.214.894 0 1.77-.066 2.633-.182V29.301z"/></svg>
  );
}

const ACTION_CONFIG = {
  google: { label: "Laisser un avis Google", Logo: GoogleLogo, confirm: "J'ai laissé mon avis" },
  instagram: { label: "S'abonner sur Instagram", Logo: InstagramLogo, confirm: "Je me suis abonné(e)" },
  facebook: { label: "Suivre sur Facebook", Logo: FacebookLogo, confirm: "Je me suis abonné(e)" }
};

export default function SpinPage() {
  const { merchantId } = useParams();
  const wheelRef = useRef(null);
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', consent: false });
  const [selectedAction, setSelectedAction] = useState('');
  const [actionConfirmed, setActionConfirmed] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (merchantId === 'demo') {
      setConfig({
        merchant_name: 'Le Petit Bistrot',
        slots: [
          { id: '1', label: 'Cafe offert', color: '#7C3AED', probability: 20, is_losing: false },
          { id: '2', label: '-10%', color: '#F59E0B', probability: 25, is_losing: false },
          { id: '3', label: 'Perdu', color: '#6B21A8', probability: 20, is_losing: true },
          { id: '4', label: 'Dessert offert', color: '#10B981', probability: 15, is_losing: false },
          { id: '5', label: 'Perdu', color: '#1E3A5F', probability: 10, is_losing: true },
          { id: '6', label: 'Boisson offerte', color: '#EC4899', probability: 10, is_losing: false },
        ],
        social_action: 'choice',
        google_review_url: 'https://search.google.com/local/writereview?placeid=ChIJTc16GMdv5kcRFhie3BTZmnQ',
        instagram_url: 'https://www.instagram.com/tap_nfc?igsh=cGVkampkdnJ0c2J3',
        facebook_url: 'https://www.facebook.com/share/1KwY5e9S6z/?mibextid=wwXIfr',
        collect_field: 'phone',
        anti_cheat_enabled: true
      });
      setLoading(false);
      return;
    }
    axios.get(`${API}/spin/${merchantId}/config`)
      .then(res => { setConfig(res.data); setSelectedAction(res.data.social_action !== 'choice' ? res.data.social_action : ''); })
      .catch(err => setError(err.response?.data?.detail || "Commerce introuvable"))
      .finally(() => setLoading(false));
  }, [merchantId]);

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setStep(2);
  };

  const getActionUrl = useCallback(() => {
    if (!config) return '#';
    const action = selectedAction || config.social_action;
    if (action === 'google') return config.google_review_url;
    if (action === 'instagram') return config.instagram_url;
    if (action === 'facebook') return config.facebook_url;
    return '#';
  }, [config, selectedAction]);

  const handleSpin = async () => {
    if (spinning || !wheelRef.current) return;
    setSpinning(true);
    setSubmitting(true);

    try {
      if (merchantId === 'demo') {
        const slots = config.slots;
        const total = slots.reduce((s, sl) => s + sl.probability, 0);
        let r = Math.random() * total, cum = 0, idx = 0;
        for (let i = 0; i < slots.length; i++) {
          cum += slots[i].probability;
          if (r <= cum) { idx = i; break; }
        }
        const slot = slots[idx];
        const demoResult = {
          slot_index: idx,
          prize: slot.is_losing ? 'Perdu' : slot.label,
          is_losing: slot.is_losing,
          prize_code: (!slot.is_losing && config.anti_cheat_enabled) ? `SW-${Math.random().toString(36).substring(2, 8).toUpperCase()}` : null,
        };
        setResult(demoResult);
        setSubmitting(false);
        wheelRef.current.spin(idx);
        return;
      }

      const { data } = await axios.post(`${API}/spin/${merchantId}/play`, {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email,
        action_performed: selectedAction || config.social_action
      });
      setSubmitting(false);
      setResult(data);
      wheelRef.current.spin(data.slot_index);
    } catch (err) {
      setSpinning(false);
      setSubmitting(false);
      setResult({ error: err.response?.data?.detail || "Erreur" });
      setStep(4);
    }
  };

  const handleSpinEnd = useCallback((index) => {
    setSpinning(false);
    setStep(4);
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0F0F1A' }}>
      <div className="w-12 h-12 border-3 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: '#0F0F1A' }}>
      <div>
        <Frown className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Oops !</h2>
        <p className="text-[#94A3B8]">{error}</p>
      </div>
    </div>
  );

  const effectiveAction = selectedAction || config?.social_action;

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ background: '#0F0F1A' }}>
      {/* Ambient effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #7C3AED, transparent 70%)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #F59E0B, transparent 70%)' }} />
      </div>

      {/* Header */}
      <div className="text-center pt-5 pb-2 px-4 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-xs shadow-lg shadow-purple-500/20">S</div>
          <span className="font-extrabold text-lg text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
        </div>
        <p className="text-[#F59E0B] text-sm font-semibold">{config?.merchant_name}</p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-6 relative z-10">
        <AnimatePresence mode="wait">
          {/* ═══ Step 1: Info ═══ */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.4 }}
              className="w-full max-w-sm" data-testid="spin-step-1">
              <div className="rounded-2xl p-6 border border-white/[0.08]" style={{ background: 'rgba(26,26,46,0.8)', backdropFilter: 'blur(20px)' }}>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#7C3AED]/15 flex items-center justify-center mb-3">
                    <Sparkles className="w-7 h-7 text-[#F59E0B]" />
                  </div>
                  <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>Tentez votre chance !</h2>
                  <p className="text-sm text-[#94A3B8] mt-1">Remplissez vos coordonnées pour jouer</p>
                </div>
                <form onSubmit={handleStep1} className="space-y-3.5">
                  <div>
                    <Label className="text-[#E2E8F0] text-xs font-medium">Prénom</Label>
                    <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} required
                      className="mt-1 bg-white/[0.06] border-white/10 text-white h-11 rounded-xl focus:ring-[#7C3AED] focus:border-[#7C3AED] placeholder:text-[#64748B]"
                      data-testid="spin-firstname-input" placeholder="Jean" />
                  </div>
                  <div>
                    <Label className="text-[#E2E8F0] text-xs font-medium">Nom</Label>
                    <Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} required
                      className="mt-1 bg-white/[0.06] border-white/10 text-white h-11 rounded-xl focus:ring-[#7C3AED] focus:border-[#7C3AED] placeholder:text-[#64748B]"
                      data-testid="spin-lastname-input" placeholder="Dupont" />
                  </div>
                  {(config?.collect_field === 'phone' || config?.collect_field === 'both') && (
                    <div>
                      <Label className="text-[#E2E8F0] text-xs font-medium">Téléphone</Label>
                      <PhoneInput
                        international
                        defaultCountry="FR"
                        countries={['FR', 'BE']}
                        value={form.phone}
                        onChange={val => setForm(f => ({ ...f, phone: val || '' }))}
                        className="phone-input-dark mt-1"
                        data-testid="spin-phone-input"
                      />
                    </div>
                  )}
                  {(config?.collect_field === 'email' || config?.collect_field === 'both') && (
                    <div>
                      <Label className="text-[#E2E8F0] text-xs font-medium">Email</Label>
                      <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                        className="mt-1 bg-white/[0.06] border-white/10 text-white h-11 rounded-xl focus:ring-[#7C3AED] focus:border-[#7C3AED] placeholder:text-[#64748B]"
                        data-testid="spin-email-input" placeholder="email@exemple.com" />
                    </div>
                  )}
                  <div className="flex items-start gap-2.5 pt-1">
                    <Checkbox checked={form.consent} onCheckedChange={v => setForm(f => ({ ...f, consent: v }))}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]" data-testid="spin-consent-checkbox" />
                    <span className="text-[11px] text-[#94A3B8] leading-tight">J'accepte que mes données soient utilisées conformément à la politique de confidentialité (RGPD)</span>
                  </div>
                  <Button type="submit" disabled={!form.consent}
                    className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl py-5 text-base font-bold shadow-lg shadow-purple-500/25 mt-2"
                    data-testid="spin-continue-btn">
                    Continuer <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </div>
            </motion.div>
          )}

          {/* ═══ Step 2: Social Action — SIMPLE FLOW ═══ */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.4 }}
              className="w-full max-w-sm" data-testid="spin-step-2">
              <div className="rounded-2xl p-6 border border-white/[0.08]" style={{ background: 'rgba(26,26,46,0.8)', backdropFilter: 'blur(20px)' }}>
                <div className="text-center mb-5">
                  <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                    {!selectedAction ? 'Une petite action d\'abord' : 'C\'est fait ?'}
                  </h2>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    {!selectedAction
                      ? (config?.social_action === 'choice' ? 'Choisissez une action pour débloquer la roue' : ACTION_CONFIG[config?.social_action]?.label)
                      : 'Confirmez que vous avez effectué l\'action'}
                  </p>
                </div>

                {/* Show action buttons (choice mode and no selection yet) */}
                {!selectedAction && config?.social_action === 'choice' && (
                  <div className="space-y-3">
                    {['google', 'instagram', 'facebook'].map(action => {
                      const cfg = ACTION_CONFIG[action];
                      const url = action === 'google' ? config.google_review_url : action === 'instagram' ? config.instagram_url : config.facebook_url;
                      if (!url) return null;
                      return (
                        <a key={action} href={url} target="_blank" rel="noopener noreferrer"
                          onClick={() => setTimeout(() => setSelectedAction(action), 500)}
                          className="w-full p-4 rounded-xl border border-white/10 hover:border-white/25 bg-white/[0.03] hover:bg-white/[0.06] flex items-center gap-4 transition-all block active:scale-[0.98]"
                          data-testid={`spin-action-${action}`}>
                          <cfg.Logo size={32} />
                          <span className="text-sm text-white font-semibold flex-1">{cfg.label}</span>
                          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Single action mode (not choice): show link + confirm below */}
                {!selectedAction && config?.social_action !== 'choice' && (() => {
                  const action = config.social_action;
                  const cfg = ACTION_CONFIG[action];
                  const url = action === 'google' ? config.google_review_url : action === 'instagram' ? config.instagram_url : config.facebook_url;
                  return (
                    <div className="space-y-4">
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        onClick={() => setTimeout(() => setSelectedAction(action), 500)}
                        className="w-full p-5 rounded-xl border border-white/10 hover:border-white/25 bg-white/[0.03] hover:bg-white/[0.06] flex items-center gap-4 transition-all block active:scale-[0.98]"
                        data-testid={`spin-action-${action}`}>
                        <cfg.Logo size={36} />
                        <span className="text-base text-white font-semibold">{cfg.label}</span>
                        <ArrowRight className="w-5 h-5 text-[#94A3B8]" />
                      </a>
                      <p className="text-xs text-[#94A3B8] text-center">Cliquez sur le bouton ci-dessus pour effectuer l'action</p>
                    </div>
                  );
                })()}

                {/* After clicking: show confirmation + spin */}
                {selectedAction && (() => {
                  const SelectedLogo = ACTION_CONFIG[selectedAction]?.Logo;
                  return (
                  <div className="space-y-4">
                    <a href={getActionUrl()} target="_blank" rel="noopener noreferrer"
                      className="w-full p-3 rounded-xl border border-[#7C3AED]/20 bg-[#7C3AED]/5 flex items-center gap-3 transition-all block active:scale-[0.98] hover:bg-[#7C3AED]/10"
                      data-testid="spin-action-reopen">
                      {SelectedLogo && <SelectedLogo size={24} />}
                      <span className="text-xs text-[#E2E8F0] flex-1">{ACTION_CONFIG[selectedAction]?.label}</span>
                      <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
                    </a>
                    <div className="flex items-center gap-2.5 p-4 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                      <Checkbox checked={actionConfirmed} onCheckedChange={setActionConfirmed}
                        className="border-white/20 data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981] w-5 h-5" data-testid="spin-action-confirmed-checkbox" />
                      <span className="text-sm text-[#E2E8F0] font-medium">{ACTION_CONFIG[selectedAction]?.confirm}</span>
                    </div>
                    <Button onClick={() => setStep(3)} disabled={!actionConfirmed}
                      className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0F0F1A] rounded-xl py-5 text-base font-bold shadow-xl shadow-amber-500/25 transition-all hover:-translate-y-0.5 disabled:opacity-40"
                      data-testid="spin-go-btn">
                      Tourner la roue ! <Sparkles className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                  );
                })()}
              </div>
            </motion.div>
          )}

          {/* ═══ Step 3: SPIN ═══ */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, type: 'spring', stiffness: 80 }}
              className="w-full max-w-md text-center" data-testid="spin-step-3">
              <motion.h2 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="text-2xl font-black text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                {spinning ? '... Bonne chance !' : 'Prêt(e) à tourner ?'}
              </motion.h2>
              <div className="relative mb-3">
                <SpinWheel ref={wheelRef} slots={config?.slots || []} size={Math.min(300, window.innerWidth - 80)} onSpinEnd={handleSpinEnd} preview={!spinning && step === 3} />
              </div>
              {!spinning && step === 3 && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                  <Button onClick={handleSpin} disabled={submitting}
                    className="bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:opacity-90 text-white rounded-full px-12 py-7 text-xl font-black shadow-2xl shadow-amber-500/30 transition-all hover:-translate-y-1 hover:shadow-amber-500/50"
                    data-testid="spin-button">
                    {submitting ? 'Préparation...' : 'TOURNER LA ROUE !'}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ═══ Step 4: Result ═══ */}
          {step === 4 && result && (
            <motion.div key="s4" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, type: 'spring', stiffness: 80 }}
              className="w-full max-w-sm text-center" data-testid="spin-step-4">
              {result.error ? (
                <div className="rounded-2xl p-8 border border-[#EF4444]/20" style={{ background: 'rgba(26,26,46,0.9)' }}>
                  <Frown className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-white mb-2">Erreur</h2>
                  <p className="text-[#94A3B8]">{result.error}</p>
                </div>
              ) : result.is_losing ? (
                <div className="rounded-2xl p-8 border border-white/[0.08]" style={{ background: 'rgba(26,26,46,0.9)' }}>
                  <div className="w-20 h-20 mx-auto rounded-full bg-[#94A3B8]/10 flex items-center justify-center mb-4">
                    <Frown className="w-10 h-10 text-[#94A3B8]" />
                  </div>
                  <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Pas de chance cette fois !</h2>
                  <p className="text-[#94A3B8] text-sm">Merci d'avoir joué. Revenez tenter votre chance !</p>
                </div>
              ) : (
                <div className="rounded-2xl p-8 border relative overflow-hidden" style={{ background: 'rgba(26,26,46,0.95)', borderColor: 'rgba(245,158,11,0.3)', boxShadow: '0 0 60px rgba(245,158,11,0.15)' }}>
                  {/* Win glow */}
                  <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at center, #F59E0B, transparent 70%)' }} />
                  <div className="relative z-10">
                    <div className="w-20 h-20 mx-auto rounded-full bg-[#F59E0B]/15 flex items-center justify-center mb-4 animate-bounce">
                      <Gift className="w-10 h-10 text-[#F59E0B]" />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Félicitations !</h2>
                    <p className="text-xl text-[#F59E0B] font-black mb-5">{result.prize}</p>
                    {result.prize_code && (
                      <div className="rounded-xl p-5 mb-5" style={{ background: '#0F0F1A', border: '1px solid rgba(124,58,237,0.3)' }}>
                        <p className="text-xs text-[#94A3B8] mb-2">Votre code cadeau unique :</p>
                        <p className="text-3xl font-mono font-black text-[#7C3AED] tracking-widest" data-testid="prize-code">{result.prize_code}</p>
                        {result.code_expires_at && (
                          <p className="text-xs text-[#94A3B8] mt-2">
                            Valide jusqu'au {new Date(result.code_expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20">
                      <Check className="w-4 h-4 text-[#10B981]" />
                      <span className="text-sm text-[#10B981] font-medium">Présentez cet écran au commerçant</span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
